import type {
  ActiveBudgetOverviewQuery,
  BudgetLineTransactionsQuery,
  BudgetListQuery,
  BudgetOverviewQuery,
  BudgetPerformanceQuery,
} from "@/features/budgeting/services/budgets-api"

const EMPTY = "__none__"

export const budgetQueryKeys = {
  all: ["budgets"] as const,
  lists: () => [...budgetQueryKeys.all, "list"] as const,
  list: (query: BudgetListQuery) =>
    [
      ...budgetQueryKeys.lists(),
      query.workspaceId ?? EMPTY,
      query.context ?? EMPTY,
      query.branchId ?? EMPTY,
      query.status ?? EMPTY,
      query.type ?? EMPTY,
      query.periodType ?? EMPTY,
      query.activityId ?? EMPTY,
      query.search ?? EMPTY,
      query.dateFrom ?? EMPTY,
      query.dateTo ?? EMPTY,
      query.includeArchived ?? false,
      query.page ?? 1,
      query.limit ?? 20,
      query.sortBy ?? EMPTY,
      query.sortOrder ?? EMPTY,
    ] as const,
  details: (workspaceId: string) => [...budgetQueryKeys.all, "detail", workspaceId] as const,
  detail: (workspaceId: string, id: string) => [...budgetQueryKeys.details(workspaceId), id] as const,
  notes: (workspaceId: string, id: string) => [...budgetQueryKeys.all, "notes", workspaceId, id] as const,
  history: (workspaceId: string, id: string) => [...budgetQueryKeys.all, "history", workspaceId, id] as const,
  versions: (workspaceId: string, id: string) => [...budgetQueryKeys.all, "versions", workspaceId, id] as const,
  overviews: (workspaceId: string) => [...budgetQueryKeys.all, "overview", workspaceId] as const,
  overview: (id: string, query: BudgetOverviewQuery) =>
    [
      ...budgetQueryKeys.overviews(query.workspaceId),
      "selected",
      id,
      query.dateFrom ?? EMPTY,
      query.dateTo ?? EMPTY,
      query.granularity ?? EMPTY,
    ] as const,
  activeOverview: (query: ActiveBudgetOverviewQuery) =>
    [
      ...budgetQueryKeys.overviews(query.workspaceId),
      "active",
      query.context,
      query.branchId,
      query.dateFrom ?? EMPTY,
      query.dateTo ?? EMPTY,
      query.granularity ?? EMPTY,
    ] as const,
  /** IBA-157: the account x fund performance matrix. Scoped by workspace + context + branch + window. */
  performance: (query: BudgetPerformanceQuery) =>
    [
      ...budgetQueryKeys.overviews(query.workspaceId),
      "performance",
      query.context,
      query.branchId,
      query.dateFrom ?? EMPTY,
      query.dateTo ?? EMPTY,
    ] as const,
  lineTransactions: (budgetId: string, lineId: string, query: BudgetLineTransactionsQuery) =>
    [
      ...budgetQueryKeys.all,
      "line-transactions",
      query.workspaceId,
      budgetId,
      lineId,
      query.dateFrom ?? EMPTY,
      query.dateTo ?? EMPTY,
      query.page ?? 1,
      query.limit ?? 20,
    ] as const,
  transactions: (budgetId: string, query: BudgetLineTransactionsQuery) =>
    [
      ...budgetQueryKeys.all,
      "transactions",
      query.workspaceId,
      budgetId,
      query.lineId ?? EMPTY,
      query.dateFrom ?? EMPTY,
      query.dateTo ?? EMPTY,
      query.page ?? 1,
      query.limit ?? 20,
    ] as const,
}
